import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProductVm } from 'api/catalog/product.vm';
import { getProducts } from 'api/catalog/get-products';

const ProductsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Obter estado inicial dos parâmetros da URL
  const searchTerm = searchParams.get('search') || '';

  const updateSearchParams = (newParams: { search?: string }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.search !== undefined) {
      newParams.search ? params.set('search', newParams.search) : params.delete('search');
    }

    setSearchParams(params);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    updateSearchParams({ search: newSearchTerm });
  };

  const fetchProducts = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getProducts(offset || '', searchTerm, {
      success: (response) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setProducts(reset ? response.objects : [...products, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar produtos:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchProducts(null, true);
    // eslint-disable-next-line
  }, []);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchProducts(nextOffset);
  };

  const handleSearchSubmit = () => {
    fetchProducts(null, true);
  };

  const handleRowClick = (productId: string) => {
    navigate(`/products/products/${productId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Produtos
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <TextField
              size="small"
              placeholder="Procurar produtos..."
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
              }}
              sx={{ width: 300 }}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
          </Box>

          <Button variant="contained" onClick={handleSearchSubmit} disabled={loading}>
            Procurar
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Código Opcional</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} hover onClick={() => handleRowClick(product.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{product.sku}</TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.optionalCode || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : products.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais produtos para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum produto encontrado
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProductsPage;
